<template>
  <b-row class="Dashboard mb-2">
    <b-col cols="12" lg="10" offset-lg="1">
      <Notification
        v-if="!isTimesheetSubmitEnabled"
        :show="true"
        variant="warning"
        :icon="{ name: 'exclamation', scale: 2 }"
      >
        <span class="ml-2">
          <strong>Attention:</strong>
          Timesheet submission is disabled until further notice.
          You may still file your <b>OT</b> and <b>OB forms</b> as normal.
        </span>
      </Notification>
      <Notification v-bind.sync="notification" />
      <Notification v-bind="timesheetStatusOptions" />

      <TimesheetCard
        :loadingEmployee="loadingUserEmployeeData"
        :employee="userEmployeeData"
        :timesheetData="timesheetData"
        :enableSubmitBtn="enableSubmitBtn"
        :other-details="otherTimesheetData"
        :other-details-options="otherDetailsOptions"
        @fetchData="fetchTimesheetData"
        @submitBtn="submitTimesheet"
      />
    </b-col>
  </b-row>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
// import Datepicker from '@/components/Datepicker';
import Notification from '@/components/Notification';
import TimesheetCard from '@/components/TimesheetCard';
import ResourceService from '@/services/ResourceService';
import TimesheetService from '@/services/TimesheetService';
import SystemService from '@/services/SystemService';
import timesheetStatuses from '@/mixins/timesheetStatuses';

const resource = new ResourceService();
const timesheet = new TimesheetService();
const system = new SystemService();

export default {
  name: 'Dashboard',
  components: {
    // Datepicker,
    Notification,
    TimesheetCard,
  },
  mixins: [timesheetStatuses],
  beforeRouteEnter(to, from, next) {
    system.getSettings(['enable_timesheet_submit'])
      .then(({ success, data, message }) => {
        next((vm) => {
          if (!success) {
            vm.setNotification({
              show: true,
              variant: 'danger',
              message: message || 'Unable to load system settings',
              refresh: true,
            });
            return;
          }
          vm.$store.dispatch('setSettings', data);
        });
      })
      .catch(() => next(vm => vm.setNotification({
        show: true,
        variant: 'danger',
        message: 'Unable to load system settings',
        refresh: true,
      })));
  },
  mounted() {
    const { id } = this.userData;
    if (id) {
      this.getUserEmployeeData();
      this.fetchTimesheetData();
    } else {
      this.setNotification({
        show: true,
        variant: 'danger',
        message: 'User not found',
        refresh: true,
      });
    }
  },
  data() {
    return {
      timesheetData: [],
      userEmployeeData: {},
      otherTimesheetData: {},
      loadingUserEmployeeData: false,
    };
  },
  computed: {
    timesheetStatus() {
      return this.$get(this.otherTimesheetData, 'status', null);
    },
    enableSubmitBtn() {
      const isRejected = this.$get(this.timesheetStatus, 'status_id', null) === this.rejected;
      // should have timesheet data
      return !this.$ie(this.timesheetData)
        // timesheet status is initial (no status yet) or rejected (for resubmission)
        && (this.$ie(this.timesheetStatus) || isRejected)
        // timesheet still loading data
        && !this.isLoadingRequest
        // timesheet is enabled from system settings
        && this.isTimesheetSubmitEnabled;
    },
    timesheetStatusOptions() {
      const status = this.$get(this.timesheetStatus, 'status_id', 0);
      const options = {
        show: true,
      };

      switch (status) {
        case this.pendingApproval:
          return {
            ...options,
            variant: 'dark',
            message: 'Timesheet already sent for approval',
            icon: {
              name: 'paper-plane',
            },
          };
        case this.approved:
          return {
            ...options,
            variant: 'success',
            message: 'Timesheet approved!',
            icon: {
              name: 'check',
            },
          };
        case this.rejected:
          return {
            ...options,
            variant: 'danger',
            message: 'Timesheet rejected. Review your timesheet and submit again.',
            icon: {
              name: 'ban',
            },
          };
        default:
          return { show: false };
      }
    },
    statusVariant() {
      const { status_id: id } = this.timesheetStatus;
      switch (id) {
        case this.pendingApproval:
          return 'warning';
        case this.approved:
          return 'success';
        case this.rejected:
          return 'danger';
        default:
          return 'light';
      }
    },
    otherDetailsOptions() {
      const statusId = this.$get(this.timesheetStatus, 'status_id', null);
      if (!statusId) {
        return {};
      }
      const defaultOptions = {
        size: 'sm',
        readonly: true,
        modifiers: ['text-left-align'],
        options: {
          labelColsXs: 12,
          labelColsSm: 4,
          labelCols: 6,
          inputColsSm: 8,
          inputColsLg: 7,
        },
      };
      const status = this.$get(this.timesheetStatus, 'status', null);
      const currentStatus = this.$startCase(status);
      const fields = [
        {
          ...defaultOptions,
          label: 'Status',
          html: statusId
            ? `<span class="badge badge-${this.statusVariant}">${currentStatus}</span>` : '-',
        },
      ];

      // only show comments if status is rejected
      if (statusId === this.rejected) {
        fields.push({
          ...defaultOptions,
          key: 'comments',
          label: 'Comments',
          isTextarea: true,
          value: '',
          options: {
            ...defaultOptions.options,
            rows: 5,
          },
        });
      }

      return { fields, labelClass: 'font-weight-bold' };
    },
    ...mapState(['notification']),
    ...mapGetters([
      'userData',
      'isLoadingRequest',
      'isTimesheetSubmitEnabled',
    ]),
  },
  methods: {
    /**
     * Get timesheet data for user
     */
    fetchTimesheetData() {
      const { id } = this.userData;

      this.startLoadingRequest();
      this.startLoadingPage();
      timesheet.getUserTimesheet(id)
        .then(({ success, data, ...otherData }) => {
          if (success) {
            // set timesheet data on table
            this.timesheetData = data || [];
            this.otherTimesheetData = otherData;
          }
        })
        .catch(error => this.setNotification({ variant: 'danger', message: error.message }))
        .then(() => {
          this.stopLoadingRequest();
          this.stopLoadingPage();
        });
    },
    /**
     * Send timesheet data for approval
     */
    submitTimesheet() {
      // do nothing if timesheet submission not enabled
      if (!this.isTimesheetSubmitEnabled) {
        return;
      }

      const { id } = this.userData;
      const { cutoff, month, year } = this.otherTimesheetData;

      this.startLoadingRequest();
      this.startLoadingPage();
      // Show loading notif
      this.setNotification({
        show: true,
        variant: 'light',
        message: 'Submitting Timesheet...',
        icon: { name: 'circle-notch', spin: true },
      });

      timesheet.submitForApproval(id, cutoff, month, year)
        .then(({ success, message, isWarning }) => {
          if (success) {
            // update timesheet status
            const status = {
              status_id: this.pendingApproval,
              status: 'pending_approval',
            };
            this.otherTimesheetData = Object.assign({}, this.otherTimesheetData, { status });

            this.setNotification({
              show: true,
              dismissible: true,
              variant: 'success',
              message: message || 'Timesheet sent for approval',
              icon: { name: 'check' },
            });
          } else {
            this.setNotification({
              show: true,
              dismissible: true,
              variant: isWarning ? 'warning' : 'danger',
              message: message || 'An error occurred',
              icon: { name: 'exclamation-triangle' },
            });
          }
        })
        .catch(error => this.setNotification({ variant: 'danger', message: error.message }))
        .then(() => this.$backToTop())
        .then(() => {
          this.stopLoadingRequest();
          this.stopLoadingPage();
        });
    },
    /**
     * Get odoo employee data for user
     */
    getUserEmployeeData() {
      const { id } = this.userData;
      this.loadingUserEmployeeData = true;
      resource.get(`/user/${id}`)
        .then((result) => {
          const { data } = result || {};
          // save user data
          this.userEmployeeData = data || {};
        })
        .catch((response) => {
          const { status, statusText: data } = response;
          const error = { status, data };

          const errorMessage = this.$get(error, 'data', 'An error occurred');
          this.setNotification({
            show: true,
            variant: 'danger',
            message: errorMessage,
            refresh: true,
            icon: {
              name: 'exclamation-triangle',
              class: 'text-danger',
            },
          });
        })
        .then(() => {
          this.loadingUserEmployeeData = false;
        });
    },
    ...mapActions([
      'setNotification',
      'startLoadingRequest',
      'stopLoadingRequest',
      'startLoadingPage',
      'stopLoadingPage',
    ]),
  },
};
</script>
